(() => {
  if (window.minireveCareInstalled) return;
  window.minireveCareInstalled = true;

  const choiceAttribute = 'Minireve Care choice';
  const state = { pending: null, cart: null, price: null, uncertain: true, reviewRequired: false, checkoutPending: false, navigating: false, debounceUntil: 0, refreshTimer: 0, native: new Set(), locks: new Map(), bypass: new WeakSet() };
  const nodes = () => [...document.querySelectorAll('minireve-care')];
  const config = () => nodes()[0]?.dataset;
  const pause = (ms) => new Promise((resolve) => window.setTimeout(resolve, ms));
  const careLines = (cart, cfg) => cart.items.filter((item) => String(item.product_id) === cfg.productId);
  const hasRomper = (cart) => cart.items.some((item) => item.handle === 'little-monster-romper' && item.quantity > 0);
  const path = (cfg, suffix) => `${cfg.rootUrl.replace(/\/?$/, '/')}${suffix}`;

  async function request(url, body) {
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), 15000);
    try {
      const response = await fetch(url, {
        method: body ? 'POST' : 'GET',
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { Accept: 'application/json', ...(body ? { 'Content-Type': 'application/json' } : {}) },
        ...(body ? { body: JSON.stringify(body) } : {}),
        signal: controller.signal,
      });
      const result = await response.json();
      if (!response.ok || result.errors || result.status >= 400) throw new Error('Cart request failed');
      return result;
    } finally {
      window.clearTimeout(timer);
    }
  }

  async function readCart(cfg) {
    const cart = await request(path(cfg, 'cart.js'));
    if (!Array.isArray(cart.items) || !cart.currency) throw new Error('Invalid cart');
    return cart;
  }

  function normalization(cart, cfg, selected = undefined) {
    const lines = careLines(cart, cfg);
    const keep = hasRomper(cart) && selected !== false ? lines[0] : null;
    const updates = {};
    lines.forEach((item) => {
      const quantity = item === keep ? 1 : 0;
      if (item.quantity !== quantity) updates[item.key] = quantity;
    });
    return updates;
  }

  async function normalize(cart, cfg, selected, choice) {
    let changed = false;
    for (let attempt = 0; attempt < 3; attempt += 1) {
      const updates = normalization(cart, cfg, selected);
      const attributes = choice && cart.attributes?.[choiceAttribute] !== choice ? { [choiceAttribute]: choice } : null;
      if (Object.keys(updates).length === 0 && !attributes) return { cart, changed };
      cart = await request(path(cfg, 'cart/update.js'), { updates, ...(attributes ? { attributes } : {}) });
      if (!Array.isArray(cart.items)) cart = await readCart(cfg);
      changed = true;
    }
    if (Object.keys(normalization(cart, cfg, selected)).length || (choice && cart.attributes?.[choiceAttribute] !== choice)) throw new Error('Cart changed concurrently');
    return { cart, changed };
  }

  function lockControls() {
    document.querySelectorAll('.cart-form, [data-care-checkout-controls], .cart-actions').forEach((node) => {
      if (!state.locks.has(node)) state.locks.set(node, node.inert);
      node.inert = true;
    });
    nodes().forEach((node) => {
      node.setAttribute('aria-busy', 'true');
      const checkbox = node.querySelector('[data-care-checkbox]');
      if (checkbox) checkbox.disabled = true;
    });
  }

  function unlockControls() {
    if (state.navigating) return;
    state.locks.forEach((value, node) => { node.inert = value; });
    state.locks.clear();
    if (state.reviewRequired) document.querySelectorAll('[data-care-checkout-controls]').forEach((node) => { node.inert = true; });
    nodes().forEach((node) => node.removeAttribute('aria-busy'));
  }

  function message(kind, value) {
    nodes().forEach((node) => {
      const target = node.querySelector(`[data-care-${kind}]`);
      if (!target) return;
      if (kind === 'error') target.querySelector('[data-care-error-text]').textContent = value || '';
      else target.textContent = value || '';
      target.hidden = !value;
    });
  }

  function render(cart) {
    state.cart = cart;
    nodes().forEach((node) => {
      const cfg = node.dataset;
      const lines = careLines(cart, cfg);
      const ui = node.querySelector('[data-care-ui]');
      const checkbox = node.querySelector('[data-care-checkbox]');
      ui.hidden = !hasRomper(cart) || cfg.available !== 'true';
      checkbox.checked = lines.some((item) => item.quantity > 0);
      checkbox.disabled = Boolean(state.pending) || state.uncertain || state.reviewRequired || state.navigating;
      const unitPrice = lines[0]?.final_price ?? state.price ?? Number(cfg.price);
      if (Number.isFinite(unitPrice)) {
        try {
          node.querySelector('[data-care-price]').textContent = new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: cart.currency, currencyDisplay: 'code' }).format(unitPrice / 100);
        } catch {}
      }
    });
  }

  async function waitForNativeCart() {
    for (let attempt = 0; attempt < 8; attempt += 1) {
      if (state.debounceUntil > Date.now()) await pause(state.debounceUntil - Date.now());
      const cartClass = customElements.get('cart-items-component');
      const pending = [...(cartClass?.pendingQuantityUpdates || []), ...state.native];
      if (pending.length === 0) return;
      let timer;
      try {
        await Promise.race([
          Promise.allSettled(pending),
          new Promise((_, reject) => { timer = window.setTimeout(() => reject(new Error('Cart update is taking too long')), 16000); }),
        ]);
      } finally {
        window.clearTimeout(timer);
      }
    }
    throw new Error('Cart is still updating');
  }

  async function goToCart(cfg) {
    if (typeof window.MinireveCart?.refresh === 'function') {
      await window.MinireveCart.refresh(state.cart);
      return;
    }
    state.navigating = true;
    const target = new URL(cfg.cartUrl, window.location.origin);
    if (window.location.pathname === target.pathname) window.location.reload();
    else window.location.assign(target.href);
  }

  async function perform(kind, selected) {
    const cfg = config();
    if (!cfg) return { changed: false };
    if (state.reviewRequired) {
      message('error', cfg.errorUpdate);
      if (kind === 'checkout') await goToCart(cfg);
      else unlockControls();
      return { changed: kind === 'checkout' };
    }
    lockControls();
    message('error', '');
    message('status', kind === 'choose' ? cfg.updating : cfg.loading);
    try {
      await waitForNativeCart();
      let cart = await readCart(cfg);
      let changed = false;
      const eligible = hasRomper(cart) && cfg.available === 'true';
      let choice = kind === 'choose' ? (selected ? 'selected' : 'declined') : cart.attributes?.[choiceAttribute] || 'default';
      let wanted = eligible && choice !== 'declined';
      if (kind === 'choose' && selected && !eligible) throw new Error('Unavailable');
      if (wanted && careLines(cart, cfg).length === 0) {
        const product = await request(`${cfg.productUrl}.js`);
        const variant = product.variants?.find((item) => String(item.id) === cfg.variantId);
        if (!variant?.available) throw new Error('Unavailable');
        state.price = variant.price;
        await request(path(cfg, 'cart/add.js'), { items: [{ id: Number(cfg.variantId), quantity: 1 }] });
        changed = true;
        cart = await readCart(cfg);
        if (kind !== 'choose') {
          choice = cart.attributes?.[choiceAttribute] || 'default';
          wanted = hasRomper(cart) && choice !== 'declined';
        }
      }
      const result = await normalize(cart, cfg, wanted, kind === 'choose' || eligible ? choice : undefined);
      changed ||= result.changed;
      state.uncertain = false;
      render(result.cart);
      message('status', '');
      if (changed || kind === 'choose') {
        await goToCart(cfg);
        return { changed: true, cart: result.cart };
      }
      return { changed: false, cart: result.cart };
    } catch (error) {
      state.uncertain = true;
      state.reviewRequired = true;
      try {
        const cart = await readCart(cfg);
        state.uncertain = false;
        render(cart);
      } catch {}
      message('status', '');
      message('error', error.message === 'Unavailable' ? cfg.errorUnavailable : kind === 'choose' ? cfg.errorUpdate : cfg.errorSync);
      throw error;
    } finally {
      unlockControls();
    }
  }

  function run(kind, selected) {
    if (state.pending) return state.pending;
    state.pending = perform(kind, selected).finally(() => {
      state.pending = null;
      if (state.cart) render(state.cart);
    });
    return state.pending;
  }

  window.MinireveCare = Object.freeze({
    sync: async () => {
      const result = await run('sync');
      if (state.reviewRequired) throw new Error('Cart needs a refresh before checkout');
      return result;
    },
    choose: async (selected) => {
      const result = await run('choose', Boolean(selected));
      if (state.reviewRequired) throw new Error('Cart needs a refresh before checkout');
      return result;
    },
    whenIdle: () => state.pending || Promise.resolve(),
    isReady: () => !state.pending && !state.uncertain && !state.reviewRequired && !state.navigating,
  });

  function scheduleSync() {
    window.clearTimeout(state.refreshTimer);
    state.refreshTimer = window.setTimeout(() => {
      if (!config() || state.navigating) return;
      if (state.pending) {
        state.pending.finally(scheduleSync).catch(() => {});
        return;
      }
      run('sync').catch(() => {});
    }, 80);
  }

  class MinireveCare extends HTMLElement {
    connectedCallback() {
      if (state.pending) {
        lockControls();
        return;
      }
      scheduleSync();
    }
  }
  if (!customElements.get('minireve-care')) customElements.define('minireve-care', MinireveCare);

  function isCareRow(target, line) {
    const cfg = config();
    if (!cfg) return false;
    const key = target.closest?.('[data-key]')?.dataset.key;
    const item = key ? state.cart?.items.find((entry) => entry.key === key) : state.cart?.items[Number(line) - 1];
    return item ? String(item.product_id) === cfg.productId : Boolean(key && key.split(':')[0] === cfg.variantId);
  }

  function declineCare(event) {
    event.preventDefault();
    event.stopImmediatePropagation();
    if (state.pending || state.reviewRequired || state.navigating) return;
    run('choose', false).catch(() => {});
  }

  document.addEventListener('click', (event) => {
    const remove = event.target.closest?.('.cart-items__remove');
    if (event.isTrusted && remove && isCareRow(remove)) declineCare(event);
  }, true);

  document.addEventListener('change', (event) => {
    if (!event.target.matches?.('minireve-care [data-care-checkbox]')) return;
    if (!event.isTrusted || state.pending || state.uncertain || state.reviewRequired || state.navigating) {
      if (state.cart) render(state.cart);
      return;
    }
    run('choose', event.target.checked).catch(() => {});
  });

  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!form.matches?.('form.cart-form') || !config()) return;
    if (state.bypass.has(form)) {
      state.bypass.delete(form);
      return;
    }
    const submitter = event.submitter;
    if (submitter?.name !== 'checkout') return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (state.checkoutPending || state.navigating) return;
    state.checkoutPending = true;
    const resume = async () => {
      if (state.pending) {
        const pendingResult = await state.pending;
        if (pendingResult?.changed) return;
      }
      const result = await run('checkout');
      if (result.changed) return;
      const currentForm = form.isConnected ? form : document.querySelector('form.cart-form');
      const currentSubmitter = submitter?.isConnected ? submitter : document.querySelector('[data-care-checkout-controls] button[name="checkout"]');
      if (!currentForm || !currentSubmitter) throw new Error('Checkout controls changed');
      if (!currentForm.reportValidity()) return;
      state.bypass.add(currentForm);
      currentForm.requestSubmit(currentSubmitter);
    };
    resume().catch(() => {}).finally(() => { state.checkoutPending = false; });
  }, true);

  document.addEventListener('quantity-selector:update', (event) => {
    if (Number(event.detail?.quantity) === 0 && isCareRow(event.target, event.detail?.cartLine)) {
      declineCare(event);
      return;
    }
    state.debounceUntil = Date.now() + 380;
    scheduleSync();
  }, true);
  document.addEventListener('cart-section:restored', scheduleSync);
  document.addEventListener('shopify:section:load', scheduleSync);
  document.addEventListener('visibilitychange', () => { if (!document.hidden) scheduleSync(); });
  window.addEventListener('pageshow', (event) => {
    state.navigating = false;
    state.checkoutPending = false;
    unlockControls();
    if (event.persisted && state.reviewRequired && config()) goToCart(config()).catch(() => { message('error', config()?.errorUpdate); });
    else scheduleSync();
  });

  import('@shopify/events').then(({ StandardEvents }) => {
    [StandardEvents.cartLinesUpdate, StandardEvents.cartDiscountUpdate, StandardEvents.cartNoteUpdate].forEach((name) => {
      if (!name) return;
      document.addEventListener(name, (event) => {
        if (!event.promise) {
          scheduleSync();
          return;
        }
        const pending = Promise.resolve(event.promise);
        state.native.add(pending);
        pending.finally(() => {
          state.native.delete(pending);
          scheduleSync();
        }).catch(() => {});
      });
    });
  }).catch(() => {});
  scheduleSync();
})();
